import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { API } from '@/lib/api';

const AuthContext = createContext(null);

const HEARTBEAT_INTERVAL_MS = 60000; // 60 seconds

// Admin uses localStorage (persists across browser close), staff uses sessionStorage
function getStorage(role) {
  return role === 'admin' ? localStorage : sessionStorage;
}

function readToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

function readUser() {
  const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function writeAuth(token, userData) {
  const storage = getStorage(userData.role);
  // Clear the other storage to avoid conflicts
  if (userData.role === 'admin') {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  storage.setItem('token', token);
  storage.setItem('user', JSON.stringify(userData));
}

function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    const savedUser = readUser();
    if (readToken() && savedUser) {
      setUser(savedUser);
    } else {
      clearAuth();
    }
    setLoading(false);
  }, []);

  // Heartbeat: keep session alive while tab is active
  useEffect(() => {
    if (!user) {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      return;
    }
    const ping = () => {
      const token = readToken();
      if (token) {
        API.post('/auth/session/ping').catch(() => {});
      }
    };
    ping(); // immediate first ping
    heartbeatRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [user]);

  // Auto-logout at midnight (12:00 AM IST) — only for non-admin users
  useEffect(() => {
    if (!user || user.role === 'admin') return;
    const checkMidnight = () => {
      const now = new Date();
      const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      if (istNow.getHours() === 0 && istNow.getMinutes() === 0) {
        backendLogout();
      }
    };
    const interval = setInterval(checkMidnight, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const backendLogout = async () => {
    try {
      await API.post('/auth/logout');
    } catch (e) { /* ignore */ }
    clearAuth();
    setUser(null);
    window.location.href = '/login';
  };

  const loginAdmin = async (username, password) => {
    const res = await API.post('/auth/admin-login', { username, password });
    const { token, user: userData } = res.data;
    writeAuth(token, userData);
    setUser(userData);
    return userData;
  };

  const requestOtp = async (username) => {
    const res = await API.post('/auth/request-otp', { username });
    return res.data;
  };

  const verifyOtp = async (username, otp) => {
    const res = await API.post('/auth/verify-otp', { username, otp });
    const { token, user: userData } = res.data;
    writeAuth(token, userData);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await API.post('/auth/logout');
    } catch (e) { /* ignore */ }
    clearAuth();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginAdmin, requestOtp, verifyOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
